import { useTranslation } from "react-i18next";
import { useState } from "react";
import { TextField } from "react-aria-components";
import { Loader2 } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useCreateFacility } from "./useFacilityMutations.js";
import { useFacilityCategories } from "./useFacilityCategories.js";
import { InheritCategoriesChecklist } from "./InheritCategoriesChecklist.js";
import { facilityErrorCopy } from "./facility-errors.js";

export interface FacilityInlineCreateProps {
  /** Parent to create under (null = a realm root). */
  parentId: number | null;
  /** The full parent node (null for a root create) — drives the "inherit parent categories" checklist (its
   *  `categoryCount` gates the fetch; its `name` labels the group). For "Insert above" this is the CANONICAL parent
   *  (guaranteed active + rendered), so its `categoryCount` is correct. */
  parentNode?: FacilityNode | null;
  /** Render depth so the card lines up with real tree rows at the same level (matches FacilityNodeRow indentation). */
  depth: number;
  session: AuthSession | null;
  /** Called with the created name on success (the parent announces + unmounts this card). */
  onCreated: (name: string) => void;
  /** Called to dismiss without creating (Cancel / Escape). */
  onCancel: () => void;
}

/**
 * Inline "new facility" card — the on-tree replacement for the create drawer. Rendered indented at `depth` right where
 * the new facility will live, with a name field + Create / Cancel. When the target **parent has categories**, it also
 * shows an "inherit categories" checklist (all ticked by default; untick to drop) — the new facility is created with the
 * ticked subset via the existing `categoryIds` (facility-features.md). Root create (no parent) stays name-only. Enter =
 * create, Escape = cancel. Fails closed inline (stays open on a resolved non-200, mirroring the auth transport seam).
 */
export function FacilityInlineCreate({ parentId, parentNode, depth, session, onCreated, onCancel }: FacilityInlineCreateProps) {
  const { t } = useTranslation();
  const create = useCreateFacility(session);
  const [name, setName] = useState("");

  // Inherit-categories: fetch the parent's categories only when it actually has some (the count the tree already shows).
  const parentHasCategories = (parentNode?.categoryCount ?? 0) > 0;
  const parentCats = useFacilityCategories(session, parentNode?.id ?? null, parentHasCategories);
  // Track the UNTICKED (dropped) ids — so "all inherited by default" needs no async init (empty set = keep everything).
  const [dropped, setDropped] = useState<ReadonlySet<number>>(new Set());
  const toggleDropped = (id: number) =>
    setDropped((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Block submit while the inheritance list is unresolved — BOTH `isFetching` (a warm/stale-cache background refetch;
  // the key is shared with the Manage drawer) AND `isPending` (a cold load, incl. React Query's OFFLINE-PAUSED state
  // where `isFetching` is false but there's still no data — panel). This keeps the gate in agreement with the
  // `isPending`-driven "{t("facilityManagement.create.loadingCategories")}" render. A settled error (isError, neither pending nor fetching) still
  // permits create — fail-closed to no categories (see `submit`).
  const categoriesLoading = parentHasCategories && (parentCats.isFetching || parentCats.isPending);
  const canSubmit = name.trim().length > 0 && !create.isPending && !categoriesLoading;

  const submit = () => {
    if (!canSubmit) return;
    // Only inherit when the list resolved cleanly. `!isError` keeps the POST in agreement with the render: a failed
    // refetch that retains stale `data` shows the "created without them" note AND posts `[]` (panel).
    const categoryIds =
      parentHasCategories && !parentCats.isError && parentCats.data
        ? parentCats.data.filter((c) => !dropped.has(c.id)).map((c) => c.id)
        : [];
    create.mutate({ name: name.trim(), parentId, categoryIds }, { onSuccess: () => onCreated(name.trim()) });
  };

  return (
    <div
      // Indent to match a real row at this depth (FacilityNodeRow uses the same paddingLeft + a w-5 rail spacer).
      style={{ paddingLeft: `calc(${depth} * 1.5rem)` }}
      // The tree owns arrow/Enter/Space/typeahead for treeitems — keep this form's keys away from it. Escape cancels.
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
    >
      <div className="flex items-stretch gap-1">
        <div aria-hidden="true" className="w-5 shrink-0" />
        <div className="border-primary bg-background ring-primary my-1 flex flex-1 flex-col gap-1.5 rounded-lg border px-3 py-1.5 shadow-sm ring-1">
          {/* Name + actions row (unchanged single-line layout; the checklist, when present, stacks below). */}
          <div className="flex w-full items-center gap-2">
            <TextField aria-label={t("facilityManagement.create.nameLabel")} value={name} onChange={setName} maxLength={255} autoFocus className="flex-1">
              <Input
                placeholder={t("facilityManagement.create.namePlaceholder")}
                className="h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
            </TextField>
            <Button size="sm" onPress={submit} isDisabled={!canSubmit}>
              {create.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                  {t("facilityManagement.create.creating")}
                </>
              ) : (
                t("facilityManagement.categories.create")
              )}
            </Button>
            <Button size="sm" variant="ghost" onPress={onCancel} isDisabled={create.isPending}>
              {t("other.cancelButton")}
            </Button>
          </div>

          {/* Inherit-parent-categories (only when the parent has any). Loading gates Create; a load error is
              fail-closed (create proceeds WITHOUT categories) with a visible note. */}
          {parentHasCategories ? (
            parentCats.isPending ? (
              <p className="text-muted-foreground text-xs">{t("facilityManagement.create.loadingCategories")}</p>
            ) : parentCats.isError ? (
              <p role="alert" className="text-muted-foreground text-xs">
                {t("facilityManagement.create.categoriesLoadFailed", { name: parentNode?.name ?? t("facilityManagement.create.theParent") })}
              </p>
            ) : parentCats.data && parentCats.data.length > 0 ? (
              <InheritCategoriesChecklist
                categories={parentCats.data}
                dropped={dropped}
                onToggle={toggleDropped}
                parentName={parentNode?.name ?? "the parent"}
              />
            ) : null
          ) : null}
        </div>
      </div>
      {create.isError ? (
        <p role="alert" className="text-destructive mt-0.5 ml-6 pl-1 text-xs">
          {facilityErrorCopy(create.error, t)}
        </p>
      ) : null}
    </div>
  );
}
